import { useMemo } from "react"

import { Bar } from "react-chartjs-2"
import { useSelector } from "react-redux"

import EmptyComponent from "../../../Components/Empty"
import { useIncognito } from "../../../hooks/useIncognito"
import usePrefersReducedMotion from "../../../hooks/usePrefersReducedMotion"
import { formatCount } from "../utils/formats"
import { TEXT_COLOR, valueAxis } from "./chartBase"
import { SERIES_COLORS } from "./palette"
import { barHoverReset, barRowGrid, barValueLabels } from "./plugins"

import styles from "./charts.module.less"

// A row without a series colour, as the platform's horizontal bars (the countries charts).
const DEFAULT_BAR_COLOR = { light: "#93C1FD", dark: "#225AEA" }

// The platform's 33px from row to row. chart.js keeps half a row label's height (16.8px line of a
// 14px label) above the first row and below the last, so the chart is that much taller.
const ROW_PITCH = 33
const LABEL_ROOM = 17
// A pinned axis' tick labels under the rows.
const AXIS_ROOM = 20

// Room right of the bars for a value past its bar, unless the axis is pinned.
const VALUE_ROOM = 84

// The narrowest bar track a narrow chart keeps by narrowing its label column.
const MIN_TRACK = 120

// Bars too light for white text inside them.
const LIGHT_BARS = [SERIES_COLORS.other, DEFAULT_BAR_COLOR.light]

// The page's horizontal bars, as the prototype draws them: 22px bars rounded at their end, a 14px
// label and value per row, the value past the bar or inside a bar longer than 78% of the axis; on
// hover the hovered bar stays and the others dim to half (DAA-2-25). Bars grow in from their base
// (DAA-1-20), unless the system asks for reduced motion. `rows` is [{ key, label, color, value }].
// `axis` pins the value axis with a 1px line per row at its ticks, e.g. { min: 0, max: 100, step: 50 }
// for a rate's 0% – 50% – 100% (DAA-5-04); without it no axis is drawn and it ends at the largest
// value, so the longest bar fills the track. Labels sit left-aligned in the design's 186px column.
// A row's `text` replaces its formatted value, e.g. "CC 48 · OI 27" (DAA-7-10).
const HorizontalBars = ({ rows, xFormat = formatCount, axis }) => {
  const theme = useSelector((state) => state.nav.theme)
  const incognito = useIncognito()
  const prefersReducedMotion = usePrefersReducedMotion()
  const textColor = TEXT_COLOR[theme] ?? TEXT_COLOR.light
  const colors = useMemo(
    () => rows.map((row) => row.color ?? DEFAULT_BAR_COLOR[theme]),
    [rows, theme]
  )

  const data = useMemo(() => {
    const barColor = (context) => {
      // A zero draws no mark: its rounded end would show as a sliver at the base.
      if (!context.raw) return "transparent"

      const color = colors[context.dataIndex]
      const hovered = context.chart.$hoveredBar
      const isDimmed = hovered != null && hovered !== context.dataIndex

      return isDimmed ? `${color}80` : color
    }

    return {
      labels: rows.map((row) => row.label),
      datasets: [
        {
          data: rows.map((row) => row.value),
          backgroundColor: barColor,
          // The hovered bar keeps its colour rather than chart.js's darkened hover shade.
          hoverBackgroundColor: barColor,
          barThickness: 22,
          borderRadius: 2,
          borderSkipped: "start"
        }
      ]
    }
  }, [rows, colors])

  const options = useMemo(() => {
    const range = axis ?? { min: 0, max: Math.max(1, ...rows.map((row) => row.value)) }
    const valueRoom = axis ? 0 : VALUE_ROOM
    const axisOptions = valueAxis({ theme, range, format: xFormat })

    return {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: "y",
      ...(prefersReducedMotion ? { animation: false } : {}),
      interaction: { mode: "nearest", axis: "y", intersect: false },
      // Inside the chart area only; leaving the canvas is barHoverReset's.
      onHover: (_event, elements, chart) => {
        const hovered = elements.length ? elements[0].index : null

        if (chart.$hoveredBar === hovered) return

        chart.$hoveredBar = hovered
        chart.update("none")
      },
      // Room for a value past its bar. On a pinned axis a long bar holds its value inside, so the axis
      // runs to the panel's edge, as the design's 0 / 50% / 100% lines.
      layout: { padding: { right: valueRoom } },
      plugins: {
        title: { display: false },
        legend: { display: false },
        tooltip: { enabled: false },
        "admin-bar-value-labels": {
          format: xFormat,
          texts: rows.map((row) => row.text),
          text: textColor,
          colors,
          lightBars: LIGHT_BARS
        },
        "admin-bar-row-grid": { color: axisOptions.grid.color }
      },
      scales: {
        x: {
          ...axisOptions,
          // Without a pinned axis nothing is drawn, so it takes no height from the rows either.
          display: Boolean(axis),
          // A pinned axis' 0 / 50% / 100% lines are barRowGrid's: one short line per row, as the
          // design's, not one across the whole chart.
          grid: { display: false },
          // "0%" starts at its line and "100%" ends at its own, inside the axis, as in the design.
          ticks: { ...axisOptions.ticks, align: "inner" },
          border: { display: false }
        },
        y: {
          type: "category",
          grid: { display: false },
          border: { display: false },
          // The design's label column: 170px, then 16px to the bars. On a narrow chart it gives the
          // bars at least MIN_TRACK, down to the width of the labels themselves.
          afterFit: (scale) => {
            const trackRoom = scale.chart.width - valueRoom - MIN_TRACK

            scale.width = Math.max(scale.width, Math.min(186, trackRoom))
          },
          ticks: {
            crossAlign: "far",
            padding: 0,
            font: { size: 14, weight: 500 },
            color: textColor
          }
        }
      }
    }
  }, [theme, rows, colors, xFormat, axis, textColor, prefersReducedMotion])

  if (!rows.length) return <EmptyComponent type="no_data_available" />

  return (
    <div
      className={styles.chart}
      style={{ height: rows.length * ROW_PITCH + LABEL_ROOM + (axis ? AXIS_ROOM : 0) }}
    >
      <Bar
        key={`${theme}-${incognito}`}
        data={data}
        options={options}
        plugins={
          axis ? [barRowGrid, barValueLabels, barHoverReset] : [barValueLabels, barHoverReset]
        }
      />
    </div>
  )
}

export default HorizontalBars
